import { SessionType, type SessionTypeKey } from '@/stores/sessionType'

import { CadyAddOn } from '@/lib/cady/cadyEnums'

import type { BookingSessionTypesListDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront/booking'

import { SESSION_TYPE_ORDER } from './ordering'

// === Generic (static) upgrade targets ===

/**
 * Maps each session type key to its generic upgrade target key.
 * Used across views to determine which session type a user should be
 * offered when they click "Upgrade" from their current session.
 */
const upgradeTargetKeyMap: Partial<Record<SessionTypeKey, SessionTypeKey>> = {
  [SessionType.Basic]: SessionType.Simple,
  [SessionType.BasicPlus]: SessionType.Standard,
  [SessionType.BasicMax]: SessionType.Legacy,
  [SessionType.Mini]: SessionType.Standard,
  [SessionType.Essential]: SessionType.Legacy,
  [SessionType.Simple]: SessionType.Standard,
  [SessionType.Standard]: SessionType.Legacy,
  [SessionType.Legacy]: SessionType.MostPopular,
  [SessionType.MostPopular]: SessionType.Elite,
  [SessionType.Elite]: SessionType.ElitePlus,
  [SessionType.ElitePlus]: SessionType.EliteUnlimited
}

/**
 * Returns the upgrade target session type key for a given source key,
 * or null if no upgrade path exists.
 */
export function getUpgradeTargetKey(key: SessionTypeKey): SessionTypeKey | null {
  return upgradeTargetKeyMap[key] ?? null
}

// === Catalog-aware upgrade targets ===

/**
 * Returns the next-higher session type key that is available in the catalog.
 * Returns null if the current key is already the highest available tier
 * or if the catalog has no higher tiers.
 */
export function getCatalogUpgradeTargetKey(
  currentKey: SessionTypeKey,
  sessionTypes: BookingSessionTypesListDto
): SessionTypeKey | null {
  const currentIndex = SESSION_TYPE_ORDER.indexOf(currentKey)
  if (currentIndex <= 0) return null
  for (let i = currentIndex - 1; i >= 0; i--) {
    const candidateKey = SESSION_TYPE_ORDER[i]
    if (sessionTypes[candidateKey] !== null) {
      return candidateKey
    }
  }
  return null
}

// === Add-on upgrade targets ===

/**
 * Returns the upgrade target session type key when an add-on (or
 * synthesized upgrade trigger) is selected from the Add-Ons page.
 *
 * Inputs:
 * - `currentKey` — the user's currently-selected session tier
 * - `addOnIds` — `CadyAddOn` ids contained in the add-on bundle (or, for a
 *   synthesized trigger card, a single-element set built from the trigger's
 *   identity)
 *
 * Rules per ADR 011's golden rule:
 * - Elite + Hair only or Makeup only → Elite Plus
 * - Elite + Hair AND Makeup combo → Elite Unlimited
 * - Elite Plus + Hair AND Makeup combo → Elite Unlimited
 * - Elite or Elite Plus + Go Anywhere → Elite Unlimited
 * - Other tiers + add-ons → null (no upgrade trigger)
 *
 * Used at both synthesis time (to compute the upgrade-target tier delta for
 * the trigger card's CTA price) and click time (to determine which upgrade
 * modal to show).
 */
export function resolveUpgradeTargetTier(
  currentKey: SessionTypeKey,
  addOnIds: Set<CadyAddOn>
): SessionTypeKey | null {
  const isElite = currentKey === SessionType.Elite
  const isElitePlus = currentKey === SessionType.ElitePlus
  if (!isElite && !isElitePlus) return null
  const hasHair = addOnIds.has(CadyAddOn.Hair)
  const hasMakeup = addOnIds.has(CadyAddOn.Makeup)
  const isCombo = hasHair && hasMakeup
  if (addOnIds.has(CadyAddOn.GoAnywhere)) return SessionType.EliteUnlimited
  if (isCombo) return SessionType.EliteUnlimited
  if (isElite && (hasHair || hasMakeup)) return SessionType.ElitePlus
  return null
}

// === Scene-specific upgrade targets ===

/**
 * Scene IDs grouped by category for upgrade matrix lookups.
 * Exported so `roadshowUpgrades.ts` shares a single source of truth.
 */
export const DESTINATION_SCENE_IDS = ['choose-your-destination', 'lake', 'beach', 'waterfall']
export const CITY_RUSTIC_SCENE_IDS = ['city', 'rustic']
export const OUTDOOR_SCENE_ID = 'outdoor'

/**
 * Scene-specific upgrade matrix (key-based).
 * When a user clicks a locked scene, the upgrade target depends on
 * which scene was clicked (not just the generic next tier).
 *
 * Rules (SY27 CSV — one tier at a time for studio scenes):
 * - Locked studio scene -> Simple (Basic), Standard (Simple/BasicPlus/Mini),
 *   Legacy (Standard/BasicMax/Essential), MP (Legacy), Elite (MP)
 * - Locked Outdoor -> Most Popular
 * - Locked City/Rustic -> Elite
 * - Locked Destination -> Elite Unlimited
 *
 * BasicPlus/Mini are Simple aliases; BasicMax/Essential are Standard aliases.
 * Their upgrade targets mirror those siblings per scene-selection guide
 * (`docs/implementation-guides/2026.03.12-005-scene-selection.md`).
 *
 * Falls back to generic getUpgradeTargetKey for unmapped combinations.
 */
const studioUpgradeKeyMap: Partial<Record<SessionTypeKey, SessionTypeKey>> = {
  [SessionType.Basic]: SessionType.Simple,
  [SessionType.BasicPlus]: SessionType.Standard,
  [SessionType.BasicMax]: SessionType.Legacy,
  [SessionType.Mini]: SessionType.Standard,
  [SessionType.Essential]: SessionType.Legacy,
  [SessionType.Simple]: SessionType.Standard,
  [SessionType.Standard]: SessionType.Legacy,
  [SessionType.Legacy]: SessionType.MostPopular,
  [SessionType.MostPopular]: SessionType.Elite
}

const outdoorUpgradeKeyMap: Partial<Record<SessionTypeKey, SessionTypeKey>> = {
  [SessionType.Basic]: SessionType.MostPopular,
  [SessionType.BasicPlus]: SessionType.MostPopular,
  [SessionType.BasicMax]: SessionType.MostPopular,
  [SessionType.Mini]: SessionType.MostPopular,
  [SessionType.Essential]: SessionType.MostPopular,
  [SessionType.Simple]: SessionType.MostPopular,
  [SessionType.Standard]: SessionType.MostPopular,
  [SessionType.Legacy]: SessionType.MostPopular
}

const cityRusticUpgradeKeyMap: Partial<Record<SessionTypeKey, SessionTypeKey>> = {
  [SessionType.Basic]: SessionType.Elite,
  [SessionType.BasicPlus]: SessionType.Elite,
  [SessionType.BasicMax]: SessionType.Elite,
  [SessionType.Mini]: SessionType.Elite,
  [SessionType.Essential]: SessionType.Elite,
  [SessionType.Simple]: SessionType.Elite,
  [SessionType.Standard]: SessionType.Elite,
  [SessionType.Legacy]: SessionType.Elite,
  [SessionType.MostPopular]: SessionType.Elite
}

/**
 * Returns the upgrade target session type key when a user clicks a
 * locked scene. The target depends on the scene category and the
 * user's current session type.
 */
export function getSceneUpgradeTargetKey(
  key: SessionTypeKey,
  sceneId: string
): SessionTypeKey | null {
  // We can't upgrade past Elite Unlimited
  if (key === SessionType.EliteUnlimited) return null
  if (DESTINATION_SCENE_IDS.includes(sceneId)) {
    return SessionType.EliteUnlimited
  }
  if (CITY_RUSTIC_SCENE_IDS.includes(sceneId)) {
    return cityRusticUpgradeKeyMap[key] ?? getUpgradeTargetKey(key)
  }
  if (sceneId === OUTDOOR_SCENE_ID) {
    return outdoorUpgradeKeyMap[key] ?? getUpgradeTargetKey(key)
  }
  // Studio scene — upgrade depends on current tier
  return studioUpgradeKeyMap[key] ?? getUpgradeTargetKey(key)
}
